import sanitizeHtml from 'sanitize-html';
import { camelCaseKeysDeep } from 'utils/dataUtils';
import { richText } from 'config/sanitizeHTML';
import omit from 'lodash/fp/omit';

import mockShortcodeProps from './mockShortcodeProps';

/**
 * Shortcode Parser
 * by mendezcode
 * @see https://github.com/mendezcode/shortcode-parser
 * @see https://gist.github.com/estrattonbailey/096256c9e20623b67648db2412c0e60e
 *
 * ESlint compatible version of shortcode-parser.js gist from estrattonbailey, which
 * it itself is a "Slightly modified for the browser, and refactored into ES6"
 * version of mendezcode's shortcode-parser.
 */
const regexMap = {
  /* eslint-disable indent */
  data: regexToString(/(\s+data="([[{]+.*[\]}]+)")?/),
  attrs: [
    '(\\s+', // at least one space before attribute
      '(',
        '[a-z0-9\\-_]+|', // Match any attribute key without a value
        '([a-z0-9\\-_]+)', // Match attribute key separate from value
        '\\s?=\\s?', // delimiter between attribute key and value
        '(',
          '[a-z0-9\\-_]+|', // alphanumeric attribute value
          '\\d+\\.\\d+|', // decimal attribute value
          '\'[^\']*\'|', // string attribute value delimited with ''
          '"[^"]*"|', // string attribute value delimited with ""
          '”[^”]*”', // string attribute value delimited with ””
        ')',
      ')',
    ')*',
  ].join(''),
  /* eslint-enable */
  slash: regexToString(/\s*\/\s*/),
  open: (name) => regexToString(/\[\s*%s/).replace('%s', name),
  rightBracket: '\\]',
  close: (name) => regexToString(/\[\s*\/\s*%s\s*\]/).replace('%s', name),
  content: regexToString(/(.|\n|)*?/),
  space: regexToString(/\s*/),
};

function regexToString(regex) {
  return regex.toString().slice(1, -1);
}

function typecast(argVal) {
  const val = argVal.trim().replace(/(^['"]|['"]$)/g, '');

  if (/^\d+$/.test(val)) {
    return parseInt(val, 10);
  } else if (/^\d+\.\d+$/.test(val)) {
    return parseFloat(val);
  } else if (/^(true|false)$/.test(val)) {
    return ('true' === val);
  } else if (/^undefined$/.test(val)) {
    return undefined;
  } else if (/^null$/i.test(val)) {
    return null;
  }

  return val;
}

function closeTagString(name) {
  return /^[^a-z0-9]/.test(name) ? `[${name[0].replace('$', '\\$')}]?${name.slice(1)}` : name;
}

function parseShortcode(name, buf, inline) {
  let regex;
  let match;
  let buffer = buf;
  let attr = {};
  let dataAttr = false;
  const { data, attrs, space, slash, rightBracket, open } = regexMap;

  if (inline) {
    regex = new RegExp(
      `^${open(name) + data + attrs + space + slash + rightBracket}`,
      'i',
    );
  } else {
    regex = new RegExp(
      `^${open(name) + data + attrs + space + rightBracket}`,
      'i',
    );
  }

  match = buffer.match(regex);
  while (null !== (match)) {
    dataAttr = match[2];

    if (dataAttr) {
      try {
        dataAttr = JSON.parse(match[2]);
        break;
      } catch (err) {
        break;
      }
    } else {
      const key = match[5] || match[4];
      const val = match[6] || match[5];
      const pattern = match[3];

      if (pattern) {
        const idx = buffer.lastIndexOf(pattern);
        attr[key] = (val !== undefined) ? typecast(val) : true;
        buffer = buffer.slice(0, idx) + buffer.slice(idx + pattern.length);
      } else {
        break;
      }

      match = buffer.match(regex);
    }
  }

  attr = dataAttr ? {
    ...omit(['settings'], dataAttr),
    ...dataAttr.settings || {},
  } :
    Object.keys(attr).reverse().reduce((prev, current) => ({
      ...prev,
      [current]: attr[current],
    }), {});

  buffer = buffer.replace(regex, '').replace(new RegExp(regexMap.close(closeTagString(name))), '');

  return {
    attr,
    content: inline ? buffer : buffer.replace(/(^\n|\n$)/g, ''),
  };
}

export default function createParser() {
  return {
    shortcodes: {},
    renderedShortcodes: {},
    buffer: '',
    add(name, callback) {
      this.shortcodes[name] = callback;
    },
    remove(name) {
      delete this.shortcodes[name];
    },
    parse(buf, contextProps = {}) {
      this.buffer = buf;
      this.renderedShortcodes = {};

      Object.keys(this.shortcodes).forEach((name) => {
        const {
          data,
          attrs,
          close,
          slash,
          rightBracket,
          content,
          open,
        } = regexMap;
        // Allow absence of first char if not alpha numeric. E.g. [#shortcode]...[/shortcode]
        const regex = {
          wrapper: new RegExp([
            open(name),
            data,
            attrs,
            rightBracket,
            content,
            close(closeTagString(name)),
          ].join(''), 'gi'),
          inline: new RegExp([
            open(name),
            data,
            attrs,
            slash,
            rightBracket,
          ].join(''), 'gi'),
        };

        const wrapperMatches = this.buffer.match(regex.wrapper);
        const inlineMatches = this.buffer.match(regex.inline);

        // Match shortcode wrapping content
        if (wrapperMatches) {
          for (let i = 0; i < wrapperMatches.length; i += 1) {
            const firstMatch = wrapperMatches[i];
            this.renderShortcode(name, firstMatch, contextProps);
          }
        }

        // Match self-closing shortcode
        if (inlineMatches) {
          let match = inlineMatches.shift();
          while (undefined !== match) {
            this.renderShortcode(name, match, contextProps, true);
            match = inlineMatches.shift();
          }
        }
      });

      // Sanitize buffer text
      this.buffer = sanitizeHtml(this.buffer, richText);

      // Replace placeholders
      Object.keys(this.renderedShortcodes)
        .forEach((key) => {
          this.buffer = this.buffer
            .replace(`shortcode_${key}`, this.renderedShortcodes[key]);
        });

      return this.buffer;
    },
    renderShortcode(name, match, contextProps, isInline) {
      const data = parseShortcode(name, match, isInline);

      // TODO: replace these with real data
      const placeholderProps = mockShortcodeProps[name] ? {
        ...contextProps,
        ...mockShortcodeProps[name],
        ...mockShortcodeProps[name].settings,
      } : { ...contextProps };

      // Create a unique key that is guaranteed not to match the shortcode regex
      // https://gist.github.com/gordonbrander/2230317
      const key = `_${Math.random().toString(36).substr(2, 9)}`;

      // Put in placeholder for now so we can sanitize the buffer text
      this.buffer = this.buffer.replace(
        match,
        `shortcode_${key}`,
      );
      this.renderedShortcodes[key] = this.shortcodes[name]
        .call(
          null,
          camelCaseKeysDeep({
            content: data.content,
            shortcode: name,
            ...data.attr,
            ...placeholderProps,
          }),
        );
    },
  };
}
